<template>
  <!--
      BrandWave — Sauerland Spielgeräte brand wave divider.
      Colors come exclusively from CSS tokens — never hardcoded.
      See Design Token System.html §06 Wave Visual System.
      Basic usage:
        <BrandWave />                            — duo green+amber wave
        <BrandWave variant="green" />            — single green wave
        <BrandWave variant="amber" />            — single amber wave
        <BrandWave :flip="true" />               — flip entire SVG vertically (dark→light)
        <BrandWave :height="120" />              — fixed height in px
        <BrandWave height="clamp(60px,12vw,200px)" /> — responsive CSS height
        <BrandWave :amplitude="120" />           — shallower waves (all layers)
        <BrandWave :frequency="2" />             — twice as many cycles (all layers)
      Trio variant with full per-layer control:
        <BrandWave
          variant="trio"
          :amber-amplitude="180" :amber-frequency="1" :flip-amber="true"
          :green-amplitude="120" :green-frequency="2" :flip-green="false"
          :neutral-amplitude="40" :neutral-frequency="3" :flip-neutral="true"
        />
  -->
  <div
      :class="[`brand-wave--${variant}`, { 'brand-wave--flip': flip }]"
      aria-hidden="true"
      class="brand-wave"
  >
    <svg
        :style="{ height: cssHeight }"
        :viewBox="`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`"
        class="block w-full"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
    >
      <template v-if="variant === 'duo'">
        <path :d="waveGreen" class="wave-layer-2"/>
        <path :d="waveAmber" class="wave-layer-1"/>
      </template>
      <template v-else-if="variant === 'trio'">
        <path :d="waveGreen" class="wave-layer-2"/>
        <path :d="waveAmber" class="wave-layer-1"/>
        <path :d="waveNeutral" class="wave-layer-neutral"/>
      </template>
      <template v-else-if="variant === 'green'">
        <path :d="waveGreen" class="wave-layer-green"/>
      </template>
      <template v-else-if="variant === 'neutral'">
        <path :d="waveNeutral" class="wave-layer-neutral"/>
      </template>
      <template v-else>
        <path :d="waveAmber" class="wave-layer-amber"/>
      </template>
    </svg>
  </div>
</template>
<script lang="ts" setup>
import {computed} from 'vue'

// ── ViewBox constants ─────────────────────────────────────────────────────────
const SVG_WIDTH = 1440
const SVG_HEIGHT = 400

// ── Layer baselines (as ratios of SVG_HEIGHT for clarity) ────────────────────
const BASELINE_AMBER = SVG_HEIGHT * 0.500  // 200 — foreground layer centre
const BASELINE_GREEN = SVG_HEIGHT * 0.615  // 246 — background layer, lifted
const BASELINE_NEUTRAL = SVG_HEIGHT * 0.6975 // 279 — topmost fill in trio

interface Props {
  /** 'green' | 'amber' | 'duo' | 'neutral' | 'trio' (green+amber+neutral stacked) */
  variant?: 'green' | 'amber' | 'duo' | 'neutral' | 'trio'
  /** Flip the entire SVG vertically (scaleY -1) for dark-to-light transitions */
  flip?: boolean
  /**
   * SVG render height. Accepts a px number (e.g. 150) or any CSS length string
   * (e.g. "clamp(60px, 12vw, 200px)") for responsive control.
   */
  height?: number | string
  /**
   * Global amplitude — peak deviation from each layer's baseline in SVG units.
   * ViewBox height = 400. Default 180 matches the original organic look.
   * Used as fallback when a per-layer amplitude is not provided.
   */
  amplitude?: number
  /**
   * Global frequency — full wave cycles across the full width.
   * 1 = one sine cycle (default). Fractional values produce wide arcs.
   * Used as fallback when a per-layer frequency is not provided.
   */
  frequency?: number
  // ── Per-layer overrides ───────────────────────────────────────────────────
  /** Amber layer amplitude override. Falls back to `amplitude`. */
  amberAmplitude?: number
  /** Amber layer frequency override. Falls back to `frequency`. */
  amberFrequency?: number
  /** Mirror the amber wave horizontally (right-to-left instead of left-to-right). */
  flipAmber?: boolean
  /** Green layer amplitude override. Falls back to `amplitude * 0.85`. */
  greenAmplitude?: number
  /** Green layer frequency override. Falls back to `frequency`. */
  greenFrequency?: number
  /** Mirror the green wave horizontally. */
  flipGreen?: boolean
  /** Neutral layer amplitude override. Falls back to `amplitude * 0.18`. */
  neutralAmplitude?: number
  /** Neutral layer frequency override. Falls back to `frequency`. */
  neutralFrequency?: number
  /** Mirror the neutral wave horizontally. */
  flipNeutral?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'duo',
  flip: false,
  height: 400,
  amplitude: 180,
  frequency: 1,
  flipAmber: false,
  flipGreen: false,
  flipNeutral: false,
})

/**
 * When `height` is a number, convert it to a viewport-proportional clamp() value
 * so the wave scales down at narrow viewports and preserveAspectRatio="none"
 * doesn't cause disproportionately steep waves on mobile.
 *
 * Formula: the target pixel height at desktop (SVG_WIDTH) is used as the clamp
 * maximum. The vw value keeps the aspect ratio constant. The minimum floor is
 * 40 % of the desktop height to prevent overly flat waves on very small screens.
 */
const cssHeight = computed((): string => {
  if (typeof props.height === 'string') return props.height
  const vw = ((props.height / SVG_WIDTH) * 100).toFixed(2)
  const min = Math.round(props.height * 0.4)
  return `clamp(${min}px, ${vw}vw, ${props.height}px)`
})

/**
 * Generates a closed SVG wave path using cubic Bezier segments.
 *
 * Each half-cycle is one cubic Bezier with both control points at the
 * peak/trough y-value, keeping the curve near the extremum for longer and
 * replicating the organic feel of the original hardcoded paths.
 *
 * Anchor x-coordinates are computed as exact fractions of svgWidth
 * (i / halfCycles * svgWidth) to prevent floating-point drift across segments.
 *
 * @param baseline    - Vertical centre of the wave in SVG units
 * @param amplitude   - Peak deviation from baseline
 * @param frequency   - Number of full cycles across svgWidth
 * @param invertPhase - Start with an upward arc instead of downward
 * @param flipX       - Mirror the wave horizontally (right-to-left traversal)
 * @param svgWidth    - Width of the SVG viewBox
 * @param svgHeight   - Height of the SVG viewBox
 */
function makeWavePath(
    baseline: number,
    amplitude: number,
    frequency: number,
    invertPhase: boolean,
    flipX: boolean,
    svgWidth = SVG_WIDTH,
    svgHeight = SVG_HEIGHT,
): string {
  const halfCycles = Math.max(1, Math.round(frequency * 2))

  // Anchor endpoints are exact fractions — eliminates floating-point drift
  const anchorX = (i: number) => (i / halfCycles) * svgWidth

  let d = `M${flipX ? svgWidth : 0},${baseline}`

  for (let i = 0; i < halfCycles; i++) {
    const sign = ((i % 2 === 0) !== invertPhase) ? 1 : -1
    const py = baseline + sign * amplitude

    const x0 = anchorX(i)
    const x1 = anchorX(i + 1)
    const segW = x1 - x0

    if (flipX) {
      const rx0 = svgWidth - x0
      const rx1 = svgWidth - x1
      const cp1x = rx0 - segW / 3
      const cp2x = rx0 - (segW * 2) / 3
      d += ` C${cp1x},${py} ${cp2x},${py} ${rx1},${baseline}`
    } else {
      const cp1x = x0 + segW / 3
      const cp2x = x0 + (segW * 2) / 3
      d += ` C${cp1x},${py} ${cp2x},${py} ${x1},${baseline}`
    }
  }

  d += flipX
      ? ` L0,${svgHeight} L${svgWidth},${svgHeight} Z`
      : ` L${svgWidth},${svgHeight} L0,${svgHeight} Z`

  return d
}

// Amber — foreground layer; starts downward by default
const waveAmber = computed(() => makeWavePath(
    BASELINE_AMBER,
    props.amberAmplitude ?? props.amplitude,
    props.amberFrequency ?? props.frequency,
    false,
    props.flipAmber,
))

// Green — background layer; lifted baseline, 85 % amplitude, opposite phase
const waveGreen = computed(() => makeWavePath(
    BASELINE_GREEN,
    props.greenAmplitude ?? props.amplitude * 0.85,
    props.greenFrequency ?? props.frequency,
    true,
    props.flipGreen,
))

// Neutral — topmost fill in trio; high baseline, 18 % amplitude by default
const waveNeutral = computed(() => makeWavePath(
    BASELINE_NEUTRAL,
    props.neutralAmplitude ?? props.amplitude * 0.18,
    props.neutralFrequency ?? props.frequency,
    false,
    props.flipNeutral,
))
</script>
<style scoped>
.brand-wave {
  line-height: 0;
  overflow: hidden;
}

.brand-wave--flip {
  transform: scaleY(-1);
}

/* All fill values reference design tokens — never hardcode colors here */
.wave-layer-1 {
  fill: var(--wave-amber-hi);
}

.wave-layer-2 {
  fill: var(--wave-green-hi);
}

.wave-layer-green {
  fill: var(--wave-green-hi);
}

.wave-layer-amber {
  fill: var(--wave-amber-hi);
}

.wave-layer-neutral {
  fill: var(--wave-neutral);
}
</style>
